/**
 * Orchestration Action Validator Service
 *
 * Validates AI-generated orchestration actions before they are applied.
 * Ensures each action has the correct type, required fields, and field types.
 *
 * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5, 7.6, 7.7, 7.8 (existing),
 * 3.1, 3.2, 3.3, 3.4, 9.4, 11.4 (one-shot orchestration creation)
 */

/**
 * Schema definitions for all orchestration action types.
 * Each schema specifies required fields and their expected types.
 *
 * Supported `types` values:
 *   - 'string'         JS string
 *   - 'number'         JS number (no further constraints; preserved for
 *                      byte-identical regression with existing schemas)
 *   - 'array'          JS array (Array.isArray)
 *   - 'object'         non-null, non-array JS object
 *   - 'number_or_null' positive integer (>= 1) when present, OR null,
 *                      OR undefined / omitted
 */
export const ACTION_SCHEMAS = {
  create_orchestration: {
    required: ['name', 'project_id', 'environment_id'],
    types: {
      name: 'string',
      project_id: 'number',
      environment_id: 'number',
      dataset_id: 'number_or_null',
    },
  },
  add_orchestration_step: {
    required: ['request_id', 'step_order', 'request_name'],
    types: {
      request_id: 'number',
      step_order: 'number',
      request_name: 'string',
    },
  },
  set_step_post_script: {
    required: ['step_order', 'script'],
    types: { step_order: 'number', script: 'string' },
  },
  set_step_pre_script: {
    required: ['step_order', 'script'],
    types: { step_order: 'number', script: 'string' },
  },
  set_step_header: {
    required: ['step_order', 'headers'],
    types: { step_order: 'number', headers: 'array' },
  },
  set_step_body: {
    required: ['step_order', 'body'],
    types: { step_order: 'number', body: 'string' },
  },
  set_step_auth: {
    required: ['step_order', 'auth'],
    types: { step_order: 'number', auth: 'object' },
  },
  reorder_steps: {
    required: ['new_order'],
    types: { new_order: 'array' },
  },
  remove_step: {
    required: ['step_order'],
    types: { step_order: 'number' },
  },
  set_step_params: {
    required: ['step_order', 'params'],
    types: { step_order: 'number', params: 'array' },
  },
  set_environment: {
    required: ['environment_id'],
    types: { environment_id: 'number' },
  },
  set_dataset: {
    required: ['dataset_id'],
    types: { dataset_id: 'number_or_null' },
  },
};

/**
 * Validate a single action's value against its schema.
 * @param {Object} action - Action object with `type` and `value` fields
 * @returns {boolean} Whether the action is valid
 */
function isValidAction(action) {
  // Action must be a non-null object
  if (!action || typeof action !== 'object') return false;

  // Must have a known action type
  const schema = ACTION_SCHEMAS[action.type];
  if (!schema) return false;

  // Must have a value object
  if (!action.value || typeof action.value !== 'object' || Array.isArray(action.value)) {
    return false;
  }

  // Check all required fields are present and non-null
  for (const field of schema.required) {
    if (action.value[field] === undefined || action.value[field] === null) {
      return false;
    }
  }

  // Check field types
  for (const [field, expectedType] of Object.entries(schema.types)) {
    const val = action.value[field];

    if (expectedType === 'array') {
      if (!Array.isArray(val)) return false;
    } else if (expectedType === 'object') {
      if (typeof val !== 'object' || val === null || Array.isArray(val)) return false;
    } else if (expectedType === 'number_or_null') {
      // Accept null/undefined (i.e. "no value"), or a strict positive integer.
      // Reject NaN, negatives, zero, non-integer numbers, strings, arrays, objects.
      if (val === null || val === undefined) {
        // ok — represents "no dataset bound"
      } else if (
        typeof val !== 'number' ||
        !Number.isInteger(val) ||
        val < 1
      ) {
        return false;
      }
    } else {
      // Primitive types: 'number', 'string'
      if (typeof val !== expectedType) return false;
    }
  }

  // Action-type-specific constraints beyond the schema grid.
  if (action.type === 'create_orchestration') {
    // `name` must be non-empty after trimming. Required-field and string-type
    // checks above already guarantee it is a string and not null/undefined.
    if (action.value.name.trim() === '') return false;
  }

  return true;
}

/**
 * Validate an array of orchestration actions.
 *
 * Performs two passes:
 *   1. Per-action schema filter (drops malformed actions).
 *   2. `create_orchestration` dedup: when more than one create action survives
 *      the schema filter, keep only the first by input order and discard the
 *      rest. Other action types pass through unchanged.
 *
 * @param {Array} actions - Array of action objects from AI generation
 * @returns {Array} Array of valid actions, with at most one `create_orchestration`
 */
export function validateOrchestrationActions(actions) {
  if (!Array.isArray(actions)) return [];

  const filtered = actions.filter(isValidAction);

  // Dedup pass: collapse duplicate `create_orchestration` actions to the first.
  let seenCreate = false;
  const result = [];
  for (const action of filtered) {
    if (action.type === 'create_orchestration') {
      if (seenCreate) continue;
      seenCreate = true;
    }
    result.push(action);
  }
  return result;
}
